import * as React from "react";

import { Field as FieldPrimitive } from "@base-ui-components/react";
import { cva, VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const verificationCodeLabelFieldVariants = cva(
  "bg-input aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive theme-default:active:scale-[0.99] relative box-border inline-flex w-full shrink-0 cursor-text items-center justify-center text-sm transition-[color,box-shadow] duration-150 ease-in-out outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "text-input-foreground shadow-input-resting hover:shadow-input-hover focus-within:ring-ring focus-within:ring-4",
        error:
          "text-destructive-foreground shadow-input-destructive-resting hover:shadow-input-destructive-hover focus-within:ring-destructive-border/15 focus-within:ring-4",
      },
      size: {
        default: "h-14 rounded-2xl",
        sm: "h-12 rounded-2xl",
        lg: "h-16 rounded-2xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface VerificationCodeLabelProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  error?: boolean;
  helperText?: string;
  size?: VariantProps<typeof verificationCodeLabelFieldVariants>["size"];
  variant?: VariantProps<typeof verificationCodeLabelFieldVariants>["variant"];
}

const VerificationCodeField = React.forwardRef<
  HTMLInputElement,
  VerificationCodeLabelProps
>(({ className, variant, size, error, helperText, ...props }, ref) => {
  const isDisabled = props.disabled;

  return (
    <div>
      <FieldPrimitive.Root
        className={cn(
          verificationCodeLabelFieldVariants({
            variant: error ? "error" : variant,
            size,
          }),
          "group relative items-end gap-0.5",
          isDisabled &&
            "bg-input-muted text-input-foreground cursor-not-allowed opacity-50",
          isDisabled && variant === "default" && "bg-input-muted",
          className
        )}
      >
        <div className="relative flex-1">
          <FieldPrimitive.Control
            id={props.id}
            className={cn(
              "h-14 w-full flex-1 rounded-2xl bg-transparent px-3 py-4 outline-none file:border-0 file:bg-transparent file:text-sm file:font-medium",
              "text-center",
              // Override browser autofill styles without blocking borders
              "autofill:bg-transparent",
              "autofill:[-webkit-text-fill-color:inherit]",
              "autofill:[transition:background-color_9999s_ease-in-out_0s]",
              isDisabled &&
                "bg-input-muted text-input-foreground cursor-not-allowed opacity-50",
              size === "sm" && "h-12 py-3 text-base",
              size === "lg" && "h-16 py-5 text-base"
            )}
            {...props}
            ref={ref}
          />
        </div>
      </FieldPrimitive.Root>
      {helperText && (
        <p
          className={cn(
            "mt-1.5 text-xs",
            error ? "text-destructive-foreground" : "text-muted-foreground"
          )}
        >
          {helperText}
        </p>
      )}
    </div>
  );
});

VerificationCodeField.displayName = "VerificationCodeFieldField";

export { VerificationCodeField, verificationCodeLabelFieldVariants };
